import React from 'react';
import PropTypes from 'prop-types';
import {
  TvBadge,
  TvCard,
  TvDropdownItem,
  TvDropdownMenu,
  TvDropdownToggle,
  TvUncontrolledDropdown,
  TvDiv,
  TvSpan,
  TvI
} from '@trustvid/design-system';
import LazyVideoPreview from "../../Campaign/components/LazyVideoPreview";
import SourceChannelBadge from '../../../../components/Common/SourceChannelBadge';
import {
  TEMPLATE_CARD_PREVIEW_MIN_HEIGHT_PX,
  getAspectFrameWrapStyle,
  getAspectPreviewClass,
  getAspectPreviewStyle,
} from '../../Campaign/utils/campaignPreviewFrame';

const TemplateCard = ({
  template,
  onUseTemplate,
  onExportTemplate,
  onDeleteTemplate = () => {},
  onRenameTemplate,
  onEditCategory,
  onMakeSystem,
  onMakePrivate,
  showExport = true,
  showDelete = true,
  canDeleteTemplate = false,
  useTemplateDisabled = false,
  showActions = true,
  compact = false,
  isSuperAdmin = false,
  systemActionTemplateId = null
}) => {
  const previewUrl = template.thumbnail_url;
  const hasPreview = Boolean(previewUrl && String(previewUrl).trim());
  const isSystem = Boolean(template.is_system);
  const systemActionBusy = systemActionTemplateId === template.id;
  const resolvedAspectRatio = template.aspect_ratio || '16:9';
  const aspectPreviewClass = getAspectPreviewClass(resolvedAspectRatio);
  const aspectPreviewStyle = getAspectPreviewStyle(resolvedAspectRatio);
  const aspectFrameWrapStyle = getAspectFrameWrapStyle(resolvedAspectRatio);
  const cardClassName = [
    'h-100',
    'border',
    compact ? '' : 'shadow-sm'
  ].join(' ');
  const cardBorderRadius = compact ? '10px' : '12px';
  const previewMinHeight = compact ? '178px' : `${TEMPLATE_CARD_PREVIEW_MIN_HEIGHT_PX}px`;
  const compactAspectLabel = template.aspect_ratio === '9:16' ? 'Portrait' : template.aspect_ratio === '1:1' ? 'Square' : 'Landscape';
  const sourceChannel = template.category_key || template.source_channel || template.category || 'normal';

  const handleUse = () => {
    if (!useTemplateDisabled) {
      onUseTemplate(template);
    }
  };

  const handleMenuAction = (event, action) => {
    event.preventDefault();
    event.stopPropagation();
    action();
  };

  return (
    <TvCard
      className={cardClassName}
      data-testid={`template-card-${template.id}`}
      role="button"
      tabIndex={0}
      onClick={handleUse}
      onDoubleClick={handleUse}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleUse();
        }
      }}
      style={{
        cursor: useTemplateDisabled ? 'not-allowed' : 'pointer',
        borderRadius: cardBorderRadius,
        borderColor: '#e5e7eb',
        overflow: 'visible',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        display: 'flex',
        flexDirection: 'column',
      }}
      aria-label={template.title || 'Template card'}
    >
      <TvDiv
        className="bg-light overflow-hidden p-0 position-relative"
        style={{ minHeight: previewMinHeight, flex: 1 }}
      >
        <TvDiv style={{ ...aspectFrameWrapStyle, height: '100%' }}>
          {hasPreview ? (
            <LazyVideoPreview
              src={previewUrl}
              wrapperClassName={`${aspectPreviewClass} bg-light rounded-0 overflow-hidden`}
              wrapperStyle={{ ...aspectPreviewStyle, height: '100%' }}
              videoClassName="object-fit-contain w-100 h-100 bg-dark"
              autoPlay
              muted
              loop
              controls={false}
              playOnlyWhenVisible
              placeholderText=""
            />
          ) : (
            <TvDiv
              className={`${aspectPreviewClass} bg-light rounded-0 overflow-hidden d-flex align-items-center justify-content-center text-muted small px-2 text-center`}
              style={aspectPreviewStyle}
            >
              No preview yet
            </TvDiv>
          )}
        </TvDiv>
        <TvBadge
          color="dark"
          className="position-absolute z-3 pe-none"
          style={{ top: '8px', left: '8px', fontSize: '10px', opacity: 0.85 }}
        >
          {compactAspectLabel}
        </TvBadge>
        <SourceChannelBadge
          sourceChannel={sourceChannel}
          className="position-absolute z-3 pe-none"
          style={{ bottom: '8px', left: '8px', fontSize: '10px' }}
        />
        {isSystem ? (
          <TvBadge
            color="primary"
            className="position-absolute z-3"
            aria-label='System template'
            title="System template"
            style={{ top: '8px', right: showActions ? '10px' : '8px', fontSize: '10px' }}
          >
            <TvI className="fa fa-star" aria-hidden="true"></TvI>
          </TvBadge>
        ) : null}
      </TvDiv>
      <TvDiv className="px-3 py-2 border-top mt-auto">
        <TvDiv
          className="fw-semibold"
          data-testid={`template-title-${template.id}`}
          title={template.title || 'Untitled Template'}
          style={{
            fontSize: compact ? '12px' : '13px',
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {template.title || 'Untitled Template'}
        </TvDiv>
      </TvDiv>
      {showActions && isSuperAdmin ? (
        <TvDiv className="position-absolute" style={{ right: '8px', bottom: '8px', zIndex: 3 }}>
          <TvUncontrolledDropdown className="ms-auto" onClick={(event) => event.stopPropagation()}>
            <TvDropdownToggle
              color="light"
              className="btn btn-sm border rounded-3 d-inline-flex align-items-center justify-content-center"
              style={{
                width: '36px',
                height: '36px',
                padding: 0,
                borderColor: '#d1d5db'
              }}
              data-testid={`template-overflow-${template.id}`}
              aria-label="More template actions"
              onClick={(event) => event.stopPropagation()}
            >
              <TvI className="fa fa-ellipsis-h" aria-hidden="true" />
            </TvDropdownToggle>
            <TvDropdownMenu end className="shadow border-0 rounded-3 py-1" style={{ minWidth: '190px' }}>
              {showExport && typeof onExportTemplate === 'function' ? (
                <TvDropdownItem
                  className="d-flex align-items-center gap-2 py-2"
                  onClick={(event) => handleMenuAction(event, () => onExportTemplate(template))}
                >
                  <TvI className="fa fa-solid fa-file-export text-muted" aria-hidden="true" />
                  <TvSpan>Export</TvSpan>
                </TvDropdownItem>
              ) : null}
              {typeof onRenameTemplate === 'function' ? (
                <TvDropdownItem
                  className="d-flex align-items-center gap-2 py-2"
                  onClick={(event) => handleMenuAction(event, () => onRenameTemplate(template))}
                >
                  <TvI className="fa fa-edit text-muted" aria-hidden="true" />
                  <TvSpan>Rename</TvSpan>
                </TvDropdownItem>
              ) : null}
              {typeof onEditCategory === 'function' ? (
                <TvDropdownItem
                  className="d-flex align-items-center gap-2 py-2"
                  onClick={(event) => handleMenuAction(event, () => onEditCategory(template))}
                >
                  <TvI className="fa fa-tag text-muted" aria-hidden="true" />
                  <TvSpan>Edit Category</TvSpan>
                </TvDropdownItem>
              ) : null}
              {!isSystem && typeof onMakeSystem === 'function' ? (
                <TvDropdownItem
                  className="d-flex align-items-center gap-2 py-2"
                  disabled={systemActionBusy}
                  onClick={(event) => handleMenuAction(event, () => onMakeSystem(template))}
                >
                  <TvI className="fa fa-star text-muted" aria-hidden="true" />
                  <TvSpan>Make System</TvSpan>
                </TvDropdownItem>
              ) : null}
              {isSystem && typeof onMakePrivate === 'function' ? (
                <TvDropdownItem
                  className="d-flex align-items-center gap-2 py-2"
                  disabled={systemActionBusy}
                  onClick={(event) => handleMenuAction(event, () => onMakePrivate(template))}
                >
                  <TvI className="fa fa-user text-muted" aria-hidden="true" />
                  <TvSpan>Make Private</TvSpan>
                </TvDropdownItem>
              ) : null}
              {showDelete ? (
                <TvDropdownItem
                  className="text-danger d-flex align-items-center gap-2 py-2"
                  onClick={(event) => handleMenuAction(event, () => onDeleteTemplate(template))}
                >
                  <TvI className="fa fa-trash" aria-hidden="true" />
                  <TvSpan>Delete</TvSpan>
                </TvDropdownItem>
              ) : null}
            </TvDropdownMenu>
          </TvUncontrolledDropdown>
        </TvDiv>
      ) : null}
    </TvCard>
  );
};

TemplateCard.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    title: PropTypes.string,
    thumbnail_url: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    category: PropTypes.string,
    aspect_ratio: PropTypes.string,
    is_system: PropTypes.bool
  }).isRequired,
  onUseTemplate: PropTypes.func.isRequired,
  onExportTemplate: PropTypes.func,
  onDeleteTemplate: PropTypes.func,
  onRenameTemplate: PropTypes.func,
  onEditCategory: PropTypes.func,
  onMakeSystem: PropTypes.func,
  onMakePrivate: PropTypes.func,
  showExport: PropTypes.bool,
  showDelete: PropTypes.bool,
  canDeleteTemplate: PropTypes.bool,
  useTemplateDisabled: PropTypes.bool,
  showActions: PropTypes.bool,
  compact: PropTypes.bool,
  isSuperAdmin: PropTypes.bool,
  systemActionTemplateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default TemplateCard;
